/**
 * Spark Activation API (vercel-auth-functions)
 *
 * Handles the server-side activation step that sits between access code
 * validation and Supabase OTP.
 *
 * TODO: Vercel base URLs for local, dev, qa, pr, and prod still point to dev — update when URLs are available.
 */
import { envSwitch } from './environmentUtils';
import { logError } from './logError';

import type { LineOfBusiness } from './constants';

// Generic auth utilities — re-exported here for backwards compatibility.
// New code should import directly from ./vafAuthApi.
export { activateEmail, requestAuthCode } from './vafAuthApi';

const VERCEL_BASE_URL = envSwitch({
  local: 'https://vercel-auth-functions-lower.vercel.app',
  dev: 'https://vercel-auth-functions-lower.vercel.app',
  qa: 'https://vercel-auth-functions-lower.vercel.app',
  pr: 'https://vercel-auth-functions-lower.vercel.app',
  stage: 'https://vercel-auth-functions-stage.vercel.app',
  prod: 'https://vercel-auth-functions-prod.vercel.app',
});

interface ActivatePhoneParams {
  phone: string;
  accessCode?: string;
  product?: string;
  lob?: LineOfBusiness;
  organizationSlug?: string;
  phDistinctId?: string;
}

interface ActivatePhoneResponse {
  success: boolean;
  message: string;
  limbix_user_id: string;
}

interface ActivateConfirmParams {
  supabaseUserId: string;
  accessToken: string;
  lob?: LineOfBusiness;
  phDistinctId?: string;
}

/**
 * Calls POST /api/spark/{lob}/activate/confirm to flip user_app_entitlements.status → active.
 * Must be called AFTER supabase.auth.verifyOtp() succeeds.
 */
export const activateSparkConfirm = async (
  params: ActivateConfirmParams,
): Promise<void> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/confirm`;
  const payload: Record<string, string> = { supabase_user_id: params.supabaseUserId };
  if (params.phDistinctId) {
    payload.ph_distinct_id = params.phDistinctId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkConfirm]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark confirm failed: ${response.status}`);
  }
};

/**
 * Calls POST /api/spark/{lob}/activate/phone to register the user server-side.
 * vercel-auth-functions sends the OTP itself as part of this call (PAT-1125) —
 * callers should navigate straight to OTP verification on success, not call
 * supabase.auth.signInWithOtp() again for the same activation.
 */
export const activateSparkPhone = async (
  params: ActivatePhoneParams,
): Promise<ActivatePhoneResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/phone`;
  const payload: Record<string, string> = {
    phone: params.phone,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  if (params.phDistinctId) {
    payload.ph_distinct_id = params.phDistinctId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkPhone]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark activation failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Calls POST /api/auth/login for returning user login.
 * Handles both phone and email — Vercel checks Supabase, migrates from Cortex
 * if needed, then triggers OTP/magic link server-side.
 *
 * Returns a uniform response regardless of whether user exists (HIPAA).
 *
 * `redirectTo` is the address the app asked us to send it back to once the member has
 * signed in. Passing it here is what lets the address survive the email: the auth service
 * hangs it on the callback URL it puts in the magic link, so the callback still knows it
 * even though the link opens a fresh browser tab with nothing saved in it. Leave it off
 * and the callback has to fall back to a default — which is how Sleepio members ended up
 * on the wrong screen (PAT-1286).
 *
 * It is optional because Spark shares this function and has no app to hand back to. An
 * absent value is omitted from the request entirely, so Spark's request is byte-for-byte
 * what it was before. The auth service validates whatever it receives; the check CAP does
 * on its own side is defence in depth, not the control.
 */
export const loginUser = async (
  params: ({ phone: string; product: string } | { email: string; product: string })
    & { ph_distinct_id?: string; redirectTo?: string },
): Promise<void> => {
  const url = `${VERCEL_BASE_URL}/api/auth/login`;

  // vercel-auth-functions names request-body fields in snake_case, so the camel-case
  // `redirectTo` this function takes goes on the wire as `redirect_to`.
  const { redirectTo, ...rest } = params;
  const payload: Record<string, unknown> = { ...rest };
  if (redirectTo) {
    payload.redirect_to = redirectTo;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    console.error('[loginUser] failed:', response.status, body.error);
    throw new Error(body.error || `Login failed: ${response.status}`);
  }
};
